/**
 * Work list chrome — filter groups, sort buttons, search, chip bar,
 * tag cloud, pagination. Matches the prototype's grammar:
 *   type badges use r1 (rectangular tag), status uses r3 (pill),
 *   priority is a dot + label (not a pill).
 *
 * All interaction routes through `data-action` handlers registered
 * in src/galaxy/components/action-registry.ts — never wire inline
 * onclick here.
 */

import { esc } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import type { WorkStats } from '../../api/work.js'
import { renderPeopleGroup, renderPeopleChip } from './work_list_chrome.js'

export interface WorkListMeta {
  page?: number
  page_size?: number
  total?: number
  has_more?: boolean
}

export interface WorkListFilters {
  type: string | null
  status: string | null
  priority: string | null
  // An assignee name, or the sentinel '@me' (resolved server-side). null = anyone.
  assignee: string | null
  tags: string[]
  search: string
  orderBy: 'updated_at' | 'created_at'
  order: 'DESC' | 'ASC'
  page: number
}

interface Colors {
  type: { epic: string; story: string; task: string; bug: string }
  status: { open: string; in_progress: string; done: string; cancelled: string; ready: string }
  priority: { critical: string; high: string; medium: string; low: string }
}

const COLORS: Colors = {
  type: { epic: 'pur', story: 'cyn', task: 't2', bug: 'err' },
  status: { open: 'cyn', in_progress: 'wrn', done: 'grn', cancelled: 't4', ready: 'grn' },
  priority: { critical: 'err', high: 'wrn', medium: 'cyn', low: 't4' },
}

// Icon glyph per type — matches prototype. Kept in ONE place.
export const TYPE_ICON: Record<string, string> = {
  epic: '◆', // ◆
  story: '■', // ■
  task: '▶', // ▶
  bug: '⚠', // ⚠
}

function fmtCount(n: number | undefined): string {
  return n !== undefined ? `<span class="wi-filter-count">${n}</span>` : ''
}

function filterBtn(args: {
  action: string
  arg: string | null
  label: string
  active: boolean
  count?: number
  iconHtml?: string
}): string {
  const argStr = args.arg === null ? 'null' : args.arg
  return `<button class="wi-filter-btn" aria-pressed="${args.active}" ${action(args.action, argStr)}>${args.iconHtml ?? ''}${esc(args.label)}${fmtCount(args.count)}</button>`
}

export function renderWorkFilters(
  f: WorkListFilters,
  stats?: WorkStats,
  assignees: readonly string[] = [],
): string {
  // ── Type group ──
  const typeBtns = [
    filterBtn({
      action: 'setWorkTypeFilter',
      arg: null,
      label: 'All',
      active: f.type === null,
      count: stats?.total,
    }),
    ...(['epic', 'story', 'task', 'bug'] as const).map((t) =>
      filterBtn({
        action: 'setWorkTypeFilter',
        arg: t,
        label: t.charAt(0).toUpperCase() + t.slice(1),
        active: f.type === t,
        count: stats?.byType?.[t],
        iconHtml: `<span style="color:var(--${COLORS.type[t]});margin-right:5px">${TYPE_ICON[t]}</span>`,
      }),
    ),
  ].join('')

  // ── Status group ──
  const statusBtns = [
    filterBtn({
      action: 'setWorkStatusFilter',
      arg: null,
      label: 'Any status',
      active: f.status === null,
    }),
    filterBtn({
      action: 'setWorkStatusFilter',
      arg: 'ready',
      label: 'Ready',
      active: f.status === 'ready',
      count: stats?.ready,
    }),
    filterBtn({
      action: 'setWorkStatusFilter',
      arg: 'open',
      label: 'Open',
      active: f.status === 'open',
      count: stats?.open,
    }),
    filterBtn({
      action: 'setWorkStatusFilter',
      arg: 'in_progress',
      label: 'In progress',
      active: f.status === 'in_progress',
      count: stats?.in_progress,
    }),
    filterBtn({
      action: 'setWorkStatusFilter',
      arg: 'done',
      label: 'Done',
      active: f.status === 'done',
      count: stats?.done,
    }),
  ].join('')

  // ── Priority group (dot + label, color-coded) ──
  const prioBtns = [
    filterBtn({
      action: 'setWorkPriorityFilter',
      arg: null,
      label: 'Any priority',
      active: f.priority === null,
    }),
    ...(['critical', 'high', 'medium', 'low'] as const).map((p) =>
      filterBtn({
        action: 'setWorkPriorityFilter',
        arg: p,
        label: p.charAt(0).toUpperCase() + p.slice(1),
        active: f.priority === p,
        count: stats?.byPriority?.[p],
        iconHtml: `<span style="display:inline-block;width:6px;height:6px;border-radius:99px;background:var(--${COLORS.priority[p]});margin-right:6px;vertical-align:middle"></span>`,
      }),
    ),
  ].join('')

  // ── Sort group ──
  const sortBtn = (ob: 'updated_at' | 'created_at', dir: 'DESC' | 'ASC', label: string): string =>
    `<button class="wi-filter-btn" aria-pressed="${f.orderBy === ob && f.order === dir}" ${action('setWorkSort', `${ob}:${dir}`)}>${esc(label)}</button>`
  const sortButtons = [
    sortBtn('updated_at', 'DESC', 'Updated ↓'),
    sortBtn('updated_at', 'ASC', 'Updated ↑'),
    sortBtn('created_at', 'DESC', 'Created ↓'),
    sortBtn('created_at', 'ASC', 'Created ↑'),
  ].join('')

  // ── Search input — uncontrolled (avoid re-rendering on every keystroke). ──
  // `oninput` is the last user intent. We throttle on the caller side.
  const searchInput = `<input
    id="work-search"
    type="search"
    class="wi-search"
    placeholder="Search id or title…"
    value="${esc(f.search)}"
    aria-label="Search work items"
    autocomplete="off"
    spellcheck="false"
    oninput="window.setWorkSearchInput && window.setWorkSearchInput(this.value)" />`

  return `<div class="wi-filters">
    <div class="wi-filter-row">
      <div role="group" aria-label="Filter by type" class="wi-filter-group">${typeBtns}</div>
      <div role="group" aria-label="Filter by status" class="wi-filter-group">${statusBtns}</div>
      <div role="group" aria-label="Filter by priority" class="wi-filter-group">${prioBtns}</div>
    </div>
    ${renderPeopleGroup(f, assignees)}
    <div class="wi-filter-row">
      <div role="group" aria-label="Sort order" class="wi-filter-group">${sortButtons}</div>
      <div style="flex:1"></div>
      ${searchInput}
    </div>
    ${renderActiveChips(f)}
    ${renderTagCloud(f)}
  </div>`
}

function renderActiveChips(f: WorkListFilters): string {
  const hasAny =
    f.type !== null ||
    f.status !== null ||
    f.priority !== null ||
    f.assignee !== null ||
    f.tags.length > 0 ||
    !!f.search
  if (!hasAny) {
    return ''
  }

  const chips: string[] = []
  if (f.type) {
    chips.push(
      `<span class="wi-chip" style="--wi-chip-bg:var(--${COLORS.type[f.type as keyof Colors['type']] ?? 't3'}-d);--wi-chip-c:var(--${COLORS.type[f.type as keyof Colors['type']] ?? 't3'})">${esc(f.type)}</span>`,
    )
  }
  if (f.status) {
    chips.push(
      `<span class="wi-chip" style="--wi-chip-bg:var(--${COLORS.status[f.status as keyof Colors['status']] ?? 't3'}-d);--wi-chip-c:var(--${COLORS.status[f.status as keyof Colors['status']] ?? 't3'})">${esc(f.status.replace(/_/g, ' '))}</span>`,
    )
  }
  if (f.priority) {
    chips.push(
      `<span class="wi-chip" style="--wi-chip-bg:var(--${COLORS.priority[f.priority as keyof Colors['priority']] ?? 't3'}-d);--wi-chip-c:var(--${COLORS.priority[f.priority as keyof Colors['priority']] ?? 't3'})">${esc(f.priority)}</span>`,
    )
  }
  if (f.assignee) {
    chips.push(renderPeopleChip(f.assignee))
  }
  if (f.search) {
    chips.push(`<span class="wi-chip"><span aria-hidden="true">🔍</span> ${esc(f.search)}</span>`)
  }
  for (const tag of f.tags) {
    chips.push(
      `<button class="wi-chip" style="--wi-chip-bg:var(--cyn-d);--wi-chip-c:var(--cyn)" ${action('toggleWorkTag', tag)} aria-label="Remove ${esc(tag)} tag filter">${esc(tag)}<span class="wi-chip-x" aria-hidden="true">×</span></button>`,
    )
  }

  return `<div role="status" aria-label="Active filters" class="wi-chips">
    <span class="wi-chips-label" aria-hidden="true">Active</span>
    ${chips.join('')}
    <button class="wi-chip-clear" aria-label="Clear all filters" ${action('clearWorkFilters')}>Clear all</button>
  </div>`
}

function renderTagCloud(f: WorkListFilters): string {
  // Tag cloud is populated by the caller (who knows which tags are in
  // the loaded page). It's attached via a custom element id so the
  // view can swap contents without re-rendering the whole filter bar.
  // Returns empty <div> placeholder — caller fills it in.
  return `<div id="wi-tag-cloud" data-tags="${esc(f.tags.join(','))}"></div>`
}

/** Render the tag cloud element contents given the currently-loaded item list. */
export function renderTagCloudContents(allTags: string[], activeTags: string[]): string {
  const uniq = [...new Set(allTags)].filter((t) => t).sort()
  if (uniq.length === 0) {
    return ''
  }
  const active = new Set(activeTags)
  const chips = uniq
    .map((tag) => {
      const isActive = active.has(tag)
      const style = isActive
        ? 'background:var(--cyn-d);color:var(--cyn)'
        : 'background:var(--s3);color:var(--t3)'
      return `<button class="wi-tag" style="${style}" aria-pressed="${isActive}" ${action('toggleWorkTag', tag)}>${esc(tag)}</button>`
    })
    .join('')
  return `<div class="wi-tag-cloud">
    <span class="wi-tag-cloud-label">Tags</span>
    ${chips}
  </div>`
}
